import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Divider, LinearProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { applyTemplate, getPlan, getTemplates, planningError, saveInvitationDesign } from '../actions/planning';
import { updateEvent } from '../actions/events';
import PlanningPulse from '../components/PlanningPulse';
import InvitationArtwork from '../components/InvitationArtwork';
import { useAlert } from '../contexts/AlertContext';
import { dateBounds } from '../utils/planningDates';

export default function EventSetup() {
  const { eventId } = useParams();
  const { showAlert } = useAlert();
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState('');
  const [budget, setBudget] = useState('');
  const [design, setDesign] = useState({ location: '', invitation_message: '', artwork: 'aurora' });
  const [date, setDate] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const [inspirationHidden, setInspirationHidden] = useState(false);
  useEffect(() => {
    let active = true;
    setError('');
    Promise.all([getPlan(eventId), getTemplates()]).then(([plan, catalog]) => {
      if (!active) return;
      setData(plan);
      try {
        if (plan.plan?.template_id || (Number(plan.event.total_budget) > 0 && plan.counts.tasks > 0 && plan.counts.guests > 0)) {
          localStorage.setItem(`planning-inspiration-hidden:${eventId}`, 'true');
        }
        setInspirationHidden(localStorage.getItem(`planning-inspiration-hidden:${eventId}`) === 'true');
      } catch {
        setError('Your browser could not load the saved inspiration preference.');
      }
      setTemplates(catalog);
      setDate(plan.event.date);
      setDesign({ location: plan.plan?.location || '', invitation_message: plan.plan?.invitation_message || '', artwork: plan.plan?.artwork || 'aurora' });
      setBudget(String(plan.event.total_budget || ''));
    }).catch(reason => { if (active) setError(planningError(reason)); });
    return () => { active = false; };
  }, [eventId, revision]);
  const perform = async (action, message) => {
    setBusy(true);
    setError('');
    try { await action(); showAlert(message, 'success'); setRevision(value => value + 1); }
    catch (reason) { setError(planningError(reason)); }
    finally { setBusy(false); }
  };
  const template = templates.find(item => item.id === selected);
  const hideInspiration = () => {
    try {
      localStorage.setItem(`planning-inspiration-hidden:${eventId}`, 'true');
      setInspirationHidden(true);
    } catch {
      setError('Your browser could not save this preference. Please allow local storage and try again.');
    }
  };
  const steps = data ? [
    { label: 'Set a budget limit', done: Number(data.event.total_budget) > 0, route: 'budget' },
    { label: 'Add your first tasks', done: data.counts.tasks > 0, route: 'tasks' },
    { label: 'Build your guest list', done: data.counts.guests > 0, route: 'guests' },
  ] : [];
  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>
      <Typography variant="overline" color="primary">Make it yours</Typography>
      <Typography component="h1" variant="h4" fontWeight={750}>A great event starts here.</Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>{data?.event.title || 'Build a plan at your own pace. Nothing is sent automatically.'}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} action={!data ? <Button onClick={() => setRevision(value => value + 1)}>Retry</Button> : undefined}>{error}</Alert>}
      {!data && !error && <CircularProgress aria-label="Loading event setup" />}
      {data && <>
        <PlanningPulse key={revision} eventId={eventId} />
        {steps.some(step => !step.done) && <Card variant="outlined" sx={{ borderRadius: 4, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between"><Typography variant="h6">Your starting checklist</Typography><Typography color="text.secondary">{steps.filter(step => step.done).length} of 3</Typography></Stack>
            <LinearProgress variant="determinate" value={steps.filter(step => step.done).length / 3 * 100} sx={{ my: 2, height: 6, borderRadius: 6 }} />
            <Stack spacing={1}>{steps.filter(step => !step.done).map(step =>
              <Box key={step.route} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography>{step.label}</Typography><Button component={Link} to={`/events/${eventId}/${step.route}`}>Get started</Button>
              </Box>)}</Stack>
            <Typography variant="caption" color="text.secondary">Optional guidance, not a requirement. You can return here anytime.</Typography>
          </CardContent>
        </Card>}
        {data.isOwner && !data.plan?.template_id && !inspirationHidden && steps.some(step => !step.done) && <Card variant="outlined" sx={{ borderRadius: 4, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
              <Typography variant="h6">Start with a little inspiration</Typography>
              <Button onClick={hideInspiration} disabled={busy}>Hide inspiration</Button>
            </Stack>
            <Typography color="text.secondary" sx={{ mb: 2 }}>Reusable starter plans with practical tasks and editable expense categories.</Typography>
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1.5 }}>
                {templates.map(item => <Card variant="outlined" key={item.id} sx={{ borderRadius: 3, borderColor: selected === item.id ? 'primary.main' : 'divider', bgcolor: selected === item.id ? 'action.selected' : 'background.paper' }}>
                  <CardActionArea onClick={() => setSelected(item.id)} aria-pressed={selected === item.id} disabled={busy}>
                    <CardContent><Typography fontWeight={700}>{item.name}</Typography><Typography variant="body2" color="text.secondary">{item.description}</Typography></CardContent>
                  </CardActionArea>
                </Card>)}
              </Box>
              {template && <Box sx={{ mt: 3 }}>
                <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mb: 2 }}><Chip label={`${template.tasks.length} starter tasks`} /><Chip label={`${template.budgets.length} expense categories`} /></Stack>
                <Typography variant="body2" sx={{ mb: 1 }}>{template.tasks.map(([title]) => title).join(' / ')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{template.budgets.map(([name, percent]) => `${name} ${percent}%`).join(' / ')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Typical planning window: {template.timeline} before your event. Tasks and expenses start assigned to you.</Typography>
                {data.counts.budgets === 0 && <TextField label="Optional total budget" type="number" value={budget} onChange={e => setBudget(e.target.value)} inputProps={{ min: 0, step: '0.01', max: 999999999 }} helperText="Leave blank for zero-value placeholders. Amounts are editable estimates, not payments." fullWidth sx={{ mb: 2 }} />}
                <Alert severity="info" sx={{ mb: 2 }}>Adds to your existing plan without deleting anything. Each event can use one starter template. Deadlines follow your event date ({data.event.date}), even for events years away; past starter deadlines for upcoming events become today.</Alert>
                <Button variant="contained" disabled={busy} onClick={() => perform(() => applyTemplate(eventId, { template_id: selected, ...(data.counts.budgets === 0 && budget !== '' ? { total_budget: budget } : {}) }), 'Starter plan added')}>Use {template.name} template</Button>
              </Box>}
            </>
          </CardContent>
        </Card>}
        {data.isOwner && <Card variant="outlined" sx={{ borderRadius: 4, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>An invitation that feels like your event</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>These details are visible to anyone holding a private guest link. Private event descriptions and guest notes are never included.</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Stack spacing={2}>
                <TextField select label="Invitation artwork" value={design.artwork} onChange={e => setDesign({ ...design, artwork: e.target.value })}>{['aurora', 'sunset', 'garden'].map(value => <MenuItem key={value} value={value}>{value.charAt(0).toUpperCase() + value.slice(1)}</MenuItem>)}</TextField>
                <TextField label="Location to share with guests" value={design.location} onChange={e => setDesign({ ...design, location: e.target.value })} inputProps={{ maxLength: 300 }} />
                <TextField label="A message for your guests" multiline minRows={3} value={design.invitation_message} onChange={e => setDesign({ ...design, invitation_message: e.target.value })} inputProps={{ maxLength: 1000 }} helperText={`${design.invitation_message.length}/1000`} />
                <Button variant="contained" disabled={busy} onClick={() => perform(() => saveInvitationDesign(eventId, design), 'Invitation design saved')}>Save invitation design</Button>
                <Button component={Link} to={`/events/${eventId}/guests`}>Create private links in your guest list</Button>
              </Stack>
              <Box aria-label="Invitation preview" sx={{ borderRadius: 4, overflow: 'hidden', alignSelf: 'start' }}><InvitationArtwork compact artwork={design.artwork} title={data.event.title} date={data.event.date} location={design.location} message={design.invitation_message} /></Box>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" fontWeight={600}>Review your event date</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <TextField label="Event date" type="date" value={date} onChange={e => setDate(e.target.value)} inputProps={dateBounds()} slotProps={{ inputLabel: { shrink: true } }} />
              <Button disabled={busy || date === data.event.date} onClick={() => perform(() => updateEvent(eventId, { date }), 'Event date updated')}>Save date</Button>
            </Stack>
          </CardContent>
        </Card>}
      </>}
    </Box>
  );
}
